import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

interface Information {
    id: string;
    tag: string;
    title: string;
    contents: string;
    priority: number;
    createdAt?: { seconds: number; nanoseconds: number };
}

export default function InformationDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [info, setInfo] = useState<Information | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchInfo = async () => {
            if (!id) return;
            try {
                const docRef = doc(db, 'infomation', id);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    setInfo({ id: docSnap.id, ...docSnap.data() } as Information);
                } else {
                    console.log("No such document!");
                }
            } catch (error) {
                console.error("Error fetching document:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchInfo();
    }, [id]);

    const getTagColor = (tag: string) => {
        if (tag === '重要') return 'bg-[#6B96C3] text-white';
        if (tag === 'INFO') return 'bg-[#F4B183] text-white';
        return 'bg-gray-200 text-gray-700';
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center">読み込み中...</div>;
    }

    if (!info) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4">
                <p>お知らせが見つかりませんでした。</p>
                <button onClick={() => navigate(-1)} className="text-blue-500 underline">戻る</button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F5F5]">
            {/* Header */}
            <header className="bg-white sticky top-0 z-10 border-b border-gray-100 shadow-sm">
                <div className="max-w-md mx-auto px-4 h-14 flex items-center">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 flex items-center justify-center -ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <i className="ri-arrow-left-s-line text-2xl"></i>
                    </button>
                    <h1 className="flex-1 text-center font-bold text-gray-800 pr-8 text-base">
                        お知らせ詳細
                    </h1>
                </div>
            </header>

            <main className="max-w-md mx-auto px-4 py-6">
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                    {/* Title & Tag */}
                    <div className="flex items-start gap-3 mb-4 border-b border-gray-100 pb-4">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold whitespace-nowrap mt-0.5 ${getTagColor(info.tag)}`}>
                            {info.tag}
                        </span>
                        <h2 className="font-bold text-base text-gray-800 leading-relaxed">
                            {info.title}
                        </h2>
                    </div>

                    {/* Content */}
                    <div className="text-gray-600 text-sm leading-loose whitespace-pre-wrap">
                        {info.contents}
                    </div>
                </div>
            </main>
        </div>
    );
}
